import { createFileRoute, Link, useCanGoBack, useNavigate, useRouter } from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type RefObject,
  useLayoutEffect,
} from 'react'

import { useAuth } from '~/auth/auth-context'
import { Spinner, WorkingButton } from '~/components/working'
import { ConfirmDialog } from '~/components/confirm-dialog'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CloseIcon,
  DeleteIcon,
  DownloadIcon,
  EditIcon,
  FlameIcon,
  ListIcon,
  PauseIcon,
  PlayIcon,
  RetryIcon,
} from '~/components/icons'
import { KindMark } from '~/components/kind-mark'
import { SafeImage } from '~/components/safe-image'
import { DaySummaryTrigger } from '~/components/day-summary'
import { SetViewerNav } from '~/components/set-viewer-nav'
import { Skeleton, SkeletonBlock } from '~/components/skeleton'
import { useToast } from '~/components/toast-context'
import { useApi, useDataService, useDataState } from '~/data/data-context'
import type { Lookup } from '~/data/service'
import { saveSetCopy } from '~/lib/download-video'
import { useFacesStep } from '~/components/use-faces-step'
import type { VideoNotice } from '~/lib/video-notice'
import {
  refusalText,
  refuses,
  videoNotice,
  VIDEO_PATIENCE_MS,
  VIDEO_SILENT_RETRIES,
} from '~/lib/video-notice'
import type { LoggedSet } from '~/data/types'
import { dayKey, formatDayLabel } from '~/lib/dates'
import {
  armOpeningSet,
  knownMediaAspect,
  PHONE_ASPECT,
  rememberMediaAspect,
  SET_CHROME_TRANSITION,
  SET_MEDIA_TRANSITION,
} from '~/lib/opening-set'
import { loadSetViewerNavOpen, saveSetViewerNavOpen } from '~/lib/set-viewer-nav-open'
import { numberOfSetInRun, timelineOrder } from '~/lib/set-runs'
import { SongCard } from '~/components/song-art'
import { SidewaysRow } from '~/components/ui/sideways-row'
import { timeOf } from '~/data/nutrition'
import { recallDeck } from '~/lib/set-deck'
import { formatSetNumbers } from '~/lib/units'

interface SetViewerSearch {
  /** Which gallery this set was opened from — see `~/lib/set-deck`. */
  readonly deck?: string
}

export const Route = createFileRoute('/sets/$setId')({
  validateSearch: (search): SetViewerSearch => ({
    ...(typeof search.deck === 'string' && search.deck !== '' ? { deck: search.deck } : {}),
  }),
  component: SetViewer,
})

const SWIPE_THRESHOLD_PX = 60

/**
 * One fact about the set, worn as a pill under its numbers.
 *
 * Over a frame there is no ground to take, so a pill carries its own: a wash of white over
 * whatever the video happens to be showing, which is the one thing that reads over all of it.
 * The ones that lead somewhere brighten as the pointer comes to them; the ones that are simply
 * true of the set do not, and nothing else distinguishes them — a shape that leads and a shape
 * that says are the same shape, and the hand is what tells them apart.
 */
const VIEWER_PILL =
  'rounded-full bg-white/16 px-3 py-1.5 text-xs font-medium text-white transition-colors'

/** And one that leads somewhere, which answers the pointer coming to it. */
const VIEWER_PILL_OPENS = `${VIEWER_PILL} hover:bg-white/30`

function SetViewer() {
  const { setId } = Route.useParams()
  const { deck: deckKey } = Route.useSearch()
  const state = useDataState()
  const service = useDataService()
  const api = useApi()
  const { units, distanceUnits } = useAuth()
  const navigate = useNavigate()
  const router = useRouter()
  const canGoBack = useCanGoBack()
  const toast = useToast()

  /*
   * Whether the arrows are up, which is whether the pointer is over the viewer at all.
   *
   * Held rather than read off `:hover`. Every move between sets is a view transition, and for
   * as long as one runs the live element under the cursor is replaced by a snapshot of it — so
   * the hover stops matching, and the browser does not go looking for it again once the
   * transition ends. The arrows would drop out on every press and stay out until the mouse was
   * moved, which is the one thing somebody leafing through sets is not doing.
   */
  const [pointerNear, setPointerNear] = useState(false)
  const [navOpen, setNavOpen] = useState(loadSetViewerNavOpen)
  const liveSet = state.sets.find((candidate) => candidate.id === setId)
  // Keep the confirmed deletion's set painted until navigation has taken the viewer away.
  // The local-first delete removes it from shared state before that navigation finishes.
  const [departingSet, setDepartingSet] = useState<LoggedSet | null>(null)
  const set = liveSet ?? (departingSet?.id === setId ? departingSet : undefined)

  // The element is held twice over: a ref to drive it (a video is mutated, not re-rendered)
  // and state so the transport re-reads it the moment it mounts, rather than depending on
  // the ref happening to be filled in by then.
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [video, setVideo] = useState<HTMLVideoElement | null>(null)
  const attachVideo = useCallback((element: HTMLVideoElement | null) => {
    videoRef.current = element
    setVideo(element)
  }, [])

  /*
   * What is leafed through: the list this set was opened from, in the order it was shown in.
   * A key nothing answers to — an address typed out, a tab restored, the app come back after
   * being unloaded — leaves the timeline, which is what the viewer has always leafed through
   * and the honest answer where there was no list. So does a list this set has fallen out of.
   */
  const timeline = useMemo(() => timelineOrder(state.sets), [state.sets])
  const deck = useMemo(() => {
    const ids = recallDeck(deckKey)
    if (ids === undefined) {
      return timeline
    }
    const byId = new Map(state.sets.map((candidate) => [candidate.id, candidate]))
    const held = ids
      .map((id) => byId.get(id))
      .filter((candidate): candidate is LoggedSet => candidate !== undefined)
    return held.some((candidate) => candidate.id === setId) ? held : timeline
  }, [deckKey, setId, state.sets, timeline])
  // Whether the viewer is leafing through a list of its own rather than the whole timeline,
  // which is what puts an arrow at each end of it even where the end is where it stands.
  const leafing = deck !== timeline

  /*
   * The list runs the way it is read: back up it to the left, on down it to the right, so the
   * first thing on the page has nothing to its left and the last has nothing to its right.
   * On the timeline, whose days run newest first, that puts a newer day to the left and an
   * older one to the right — but within a day the tiles run in the order they were performed,
   * and the leafing follows what is on the screen rather than the clock. So the two are named
   * for where they are in the list, which is the one thing true of them on every gallery.
   */
  const index = deck.findIndex((candidate) => candidate.id === setId)
  const next = index >= 0 ? deck[index + 1] : undefined
  const previous = index > 0 ? deck[index - 1] : undefined

  /*
   * Moving between sets replaces rather than stacks: the viewer is one stop, so closing it
   * steps back to the timeline instead of walking back through everything just looked at.
   *
   * And it crosses nothing over. Every other navigation in the app runs a view transition, but
   * a move between sets is not a page change — it is one page showing the next thing in its
   * list, the way a gallery does. Left to cross over, the whole control strip is lifted into a
   * snapshot and faded into a snapshot of itself, and translucent controls dip through the
   * middle of that: the arrow under the cursor blinks out and back on every single press.
   */
  const goTo = (targetId: string | undefined) => {
    if (targetId !== undefined) {
      void navigate({
        to: '/sets/$setId',
        params: { setId: targetId },
        search: deckKey !== undefined ? { deck: deckKey } : {},
        replace: true,
        viewTransition: false,
      })
    }
  }

  /** Keeping the video rather than watching it — saved the way a selection of them is. */
  /*
   * Downloading goes through the faces step, which settles whose faces the copy blurs —
   * `/specs/features/faces.spec.md`. While it asks, the video here is the copy it is making: the
   * copy itself once made, carrying on from the moment the video was at, and the frame standing,
   * blurred and paused, with how far along it is, while it is being made. The copies are kept for
   * as long as the viewer stays on this set. Otherwise the video plays as filmed.
   */
  const faces = useFacesStep({ docked: true, scope: set?.id ?? '' })
  // Read by the keyboard's handler, which is not built again for every change of it.
  const facesAsking = useRef(faces.asking)
  useLayoutEffect(() => {
    facesAsking.current = faces.asking
  }, [faces.asking])
  const download = async () => {
    if (set === undefined) return
    await faces.run([set], 'Download', (copies) => {
      const copy = copies.of(set.id)
      if (copy === null) {
        toast('That video could not be downloaded.')
        return Promise.resolve(false)
      }
      saveSetCopy(copy, set)
      return Promise.resolve(true)
    })
  }
  const copyShown = set !== undefined && faces.showing?.setId === set.id ? faces.showing.copy : null
  const makingCopy = copyShown?.kind === 'making'
  /*
   * What the video plays: as filmed, or the copy last made — which stands, paused, while another
   * is being made, and plays on from the same moment once it is.
   */
  const shownCopyUrl =
    copyShown?.kind === 'made'
      ? copyShown.url
      : copyShown?.kind === 'making'
        ? copyShown.standing
        : null
  // Where this set's video last was, for a copy taking its place to carry on from.
  const lastMoment = useRef<{ setId: string; time: number; src: string } | null>(null)
  useEffect(() => {
    if (!video) return
    if (makingCopy) video.pause()
    else void video.play().catch(() => undefined)
  }, [makingCopy, video])

  /*
   * Closing goes back, so the timeline comes up where it was left. Pushing "/" instead
   * would be a fresh entry with no remembered position, landing at the top every time.
   */
  const close = useCallback(
    // The router's own escape hatch for animating an imperative back(): the write inside is
    // the documented way to ask for it, so the whole callback is exempted here.
    // eslint-disable-next-line react-hooks/immutability
    (flight = true) => {
      if (flight) {
        /*
         * A playing video is the one thing the browser cannot reliably photograph: in a
         * hardware-decoded tab, capturing it for the old snapshot can fail and take the
         * whole transition with it — the frame never flies, the page just swaps. The
         * poster under it is the very picture the tile will show, so the video steps out
         * of the shot before the shot is taken.
         */
        const video = videoRef.current
        if (video) {
          video.pause()
          video.style.transition = 'none'
          video.style.opacity = '0'
        }
        // The frame flies back into the tile of the set on screen — arrows and swipes may
        // have wandered far from the tile that was pressed, so that tile is armed afresh.
        // Going back bypasses navigate(), so the router is asked for the transition directly.
        armOpeningSet(setId)
        // eslint-disable-next-line react-hooks/immutability -- see the effect below
        router.shouldViewTransition = true
      }
      if (canGoBack) {
        router.history.back()
      } else {
        void navigate({ to: '/', viewTransition: flight })
      }
    },
    [canGoBack, router, navigate, setId],
  )

  /*
   * What the server says about a set the device is not holding. The pile the timeline has
   * pulled is a window, so a set older than it — or reached from a search, a link, an address
   * typed out — is absent from the pile without being absent from the account, and reading
   * the pile as the whole of what exists is what made those sets read as deleted.
   *
   * `null` while the asking is still out.
   */
  // Keyed by what was asked, so an answer about another id — or about the try before a
  // retry — is never read as the answer to this one.
  const [lookup, setLookup] = useState<{ about: string; ask: number; answer: Lookup } | null>(null)
  const [asked, setAsked] = useState(0)
  useEffect(() => {
    if (liveSet !== undefined || departingSet !== null) {
      return
    }
    let cancelled = false
    void service.loadSet(setId).then((answer) => {
      if (!cancelled) {
        setLookup({ about: setId, ask: asked, answer })
      }
    })
    return () => {
      cancelled = true
    }
    // `asked` is what a retry turns to run this again.
  }, [service, setId, liveSet, departingSet, asked])

  const heard =
    lookup !== null && lookup.about === setId && lookup.ask === asked ? lookup.answer : null

  // Gone is the one answer the page does not stay to show: a page about something that is not
  // there is about nothing, so it leaves and says so over wherever it lands.
  const goneToast = heard === 'gone' && liveSet === undefined && departingSet === null
  // Said once. Leaving re-reads what the router can do, so the effect runs again on the way
  // out, and a page leaving twice over would say it twice over.
  const said = useRef<string | null>(null)
  // `close` can set the router's transition flag — the same deliberate mutation the close
  // handler above is excused for, and not one it makes on this path, where there is no flight.
  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    if (!goneToast || said.current === setId) {
      return
    }
    said.current = setId
    close(false)
    toast('That set is gone')
  }, [goneToast, setId, close, toast])

  /*
   * Meant to be playing and not playing — which is what a slow start and a stall partway
   * through both are, and what a video paused on purpose is not. Read off the element rather
   * than kept: the element is the source of truth for everything else the transport shows.
   */
  const stalled = useMediaValue(video, STALL_EVENTS, (element) =>
    element === null ? false : !element.paused && element.readyState < element.HAVE_FUTURE_DATA,
  )

  // Failures this set's video has reported since it last played, counting the first one.
  const [failures, setFailures] = useState(0)
  // Whether it has been stalled long enough for that to be worth drawing.
  const [waited, setWaited] = useState(false)

  const notice: VideoNotice =
    set?.hasVideo === true
      ? videoNotice({ stalled, waited, failures, online: state.online })
      : 'nothing'
  const refused = refuses(notice)

  /** Trying the whole fetch again: what every way on means once the video has refused. */
  const retry = useCallback(() => {
    setFailures(0)
    const element = videoRef.current
    if (!element) {
      return
    }
    element.load()
    void element.play().catch(() => undefined)
  }, [])

  useEffect(() => {
    // A video is given a second before anything says it is loading. Most start inside it, and
    // a line that flashed on and off at every set moved through would say less than nothing.
    if (!stalled) {
      return
    }
    const timer = setTimeout(() => {
      setWaited(true)
    }, VIDEO_PATIENCE_MS)
    return () => {
      clearTimeout(timer)
      // The next stall gets its own second rather than inheriting this one's.
      setWaited(false)
    }
  }, [stalled])

  useEffect(() => {
    // The first failure and the two after it are answered without saying anything: from the
    // outside a quiet retry and a slow load are the same thing, so the line keeps running.
    if (failures < 1 || failures > VIDEO_SILENT_RETRIES) {
      return
    }
    const element = videoRef.current
    if (!element) {
      return
    }
    element.load()
    void element.play().catch(() => undefined)
  }, [failures])

  /*
   * Walking back into signal is the answer to the commonest reason a video would not play, so
   * it is taken as one rather than left waiting to be pressed. Only a connection that
   * returned, and only a video that is actually stuck — one that played straight through the
   * outage has nothing to start over.
   */
  const wasOnline = useRef(state.online)
  useEffect(() => {
    const returned = !wasOnline.current && state.online
    wasOnline.current = state.online
    if (returned && (failures > 0 || stalled)) {
      retry()
    }
  }, [state.online, failures, stalled, retry])

  // Escape closes the viewer, the arrow keys move between sets, space holds the video.
  const nextId = next?.id
  const previousId = previous?.id
  const [deleting, setDeleting] = useState(false)
  /** The control the list hangs off, so closing it from inside gives the focus back. */
  const navToggle = useRef<HTMLButtonElement>(null)
  /*
   * The immutability rule guards React state from being written behind React's back, and
   * follows this effect down to `close`, which sets the router's own flag for the next
   * navigation — the documented way to ask TanStack to animate an imperative history.back().
   * The rule reports at the effect that reaches the write, so the exemption belongs here.
   */
  // eslint-disable-next-line react-hooks/immutability
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // While the confirmation is up, the keyboard belongs to it. Otherwise an arrow key
      // brushed over the prompt moves the viewer underneath, and Delete then destroys the set
      // it moved on to rather than the one the prompt named.
      if (deleting) {
        return
      }
      // While the faces step asks, the viewer stays on the set it is about.
      const holding = facesAsking.current
      const onSeekBar = event.target instanceof Element && event.target.closest('input')

      if (event.key === 'Escape') {
        close()
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        const back = event.key === 'ArrowLeft'
        // Shift keeps the same left-is-back sense, one step down in scale: the plain key
        // moves between sets, holding shift moves within one.
        if (event.shiftKey) {
          event.preventDefault()
          if (refused) {
            retry()
            return
          }
          // Written straight through the ref: a local copy of it reads to the compiler as
          // an ordinary value being mutated after render, which a ref is exempt from.
          if (videoRef.current) {
            videoRef.current.currentTime = Math.max(
              0,
              videoRef.current.currentTime + (back ? -1 : 1),
            )
          }
          return
        }
        if (onSeekBar || holding) {
          // The focused seek bar answers to arrows itself.
          return
        }
        const target = back ? previousId : nextId
        if (target !== undefined) {
          // Exactly what an arrow or a swipe does — see `goTo`, which the keyboard cannot
          // reach from here without carrying the prompt's guard along with it.
          void navigate({
            to: '/sets/$setId',
            params: { setId: target },
            search: deckKey !== undefined ? { deck: deckKey } : {},
            replace: true,
            viewTransition: false,
          })
        }
      } else if (event.key === ' ') {
        // On a button or a field, space is that control's key, not the video's.
        if (event.target instanceof Element && event.target.closest('button, a, input')) {
          return
        }
        event.preventDefault()
        if (refused) {
          retry()
          return
        }
        const video = videoRef.current
        if (video) {
          if (video.paused) {
            void video.play()
          } else {
            video.pause()
          }
        }
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [nextId, previousId, navigate, videoRef, close, deckKey, deleting, refused, retry])

  const pointerStart = useRef<number | null>(null)
  // Whether the pointer that just lifted travelled like a swipe. A browser fires a click at
  // the end of a drag all the same, and a swipe's click must not also pause the video.
  const swiped = useRef(false)

  // Which set's video has actually reached playing. Keyed by set, so swiping to the next one
  // starts from its own thumbnail again instead of inheriting the last one's answer.
  const [playingFor, setPlayingFor] = useState<string | null>(null)
  const [measured, setMeasured] = useState<{ setId: string; aspect: number } | null>(null)

  if (!set) {
    if (heard === 'unreachable') {
      return (
        <section className="space-y-4 py-16">
          <h1 className="text-2xl font-medium">This set could not be reached</h1>
          <p className="text-muted">
            The connection did not answer, so there is no saying whether this set is still there.
          </p>
          <button
            type="button"
            className="text-accent underline"
            onClick={() => {
              setAsked((count) => count + 1)
            }}
          >
            Try again
          </button>
        </section>
      )
    }
    // Gone leaves rather than staying to say so, and asking is still out until it does.
    return (
      <SkeletonBlock label="Loading this set" className="space-y-4 py-16">
        <Skeleton className="h-9 w-56" />
        <Skeleton className="h-72 w-full" />
        <Skeleton className="h-5 w-40" />
      </SkeletonBlock>
    )
  }

  // The thumbnail was made here, so there is one to fly whether or not the server has it.
  const hasPoster = set.hasThumbnail

  /*
   * Which set of its exercise this was, counting from the first one performed — among the
   * workout it belonged to, or the day's sets belonging to none when it belonged to none. The
   * same number its tile wears and the tracker gave it, however far apart that exercise's sets
   * were. Only as much of the run as has been loaded can be counted, so a run only half here
   * says nothing rather than guessing a number that would change as the rest arrived.
   */
  const run = state.sets.filter((candidate) =>
    set.workoutId === null
      ? candidate.workoutId === null && dayKey(candidate.performedAt) === dayKey(set.performedAt)
      : candidate.workoutId === set.workoutId,
  )
  const ascendingRun = [...run].reverse()
  const setNumber = numberOfSetInRun(ascendingRun, set.id)

  /*
   * What the body was on the day this set was performed is the day's business rather than the
   * set's: it is behind the date, in the day summary, and nothing of it is said here.
   */
  const setDay = dayKey(set.performedAt)
  const workout =
    set.workoutId === null
      ? undefined
      : state.workouts.find((candidate) => candidate.id === set.workoutId)
  const enrollment =
    workout?.enrollmentId == null
      ? undefined
      : state.enrollments.find((candidate) => candidate.id === workout.enrollmentId)
  const workoutName = workout?.name ?? workout?.templateWorkout ?? 'Workout'
  /*
   * What the workout was run under, where it was run under anything: the template, and the
   * workout of that template this was — "Power Builder › Push". The template's own workout is
   * dropped where it is already the name being shown, since a chip reading "Push › Push" says
   * one thing twice and gives up the room to say it.
   */
  const ranUnder =
    enrollment === undefined
      ? null
      : [
          enrollment.templateName,
          ...(workout?.templateWorkout != null && workout.templateWorkout !== workoutName
            ? [workout.templateWorkout]
            : []),
        ].join(' › ')

  /*
   * What the set itself is true of, a pill apiece — see `/specs/app.spec.md`, Set viewer. Only
   * the set's own facts: the day behind the first of them is the day summary's to tell.
   *
   * A pill and not a word, because every one of these is something you can act on or read at a
   * glance, and a shape is how a thing to press says so. A pill stands only where the set
   * carries what it says, and nothing stands in for one that is absent — a greyed "No workout"
   * is a row about the lack of a workout.
   */
  const setPills: { key: string; said: string }[] =
    set.rpe === null ? [] : [{ key: 'effort', said: `RPE ${String(set.rpe)}` }]

  /*
   * The shape of the landing box, which has to be the video's own shape: anything else
   * letterboxes the video inside the box the thumbnail flew to, and the picture arrives and
   * then shrinks. Measured from this video if it has ever been played, upright otherwise —
   * which is what a set filmed on a phone is.
   */
  const mediaAspect =
    (measured?.setId === set.id ? measured.aspect : null) ??
    knownMediaAspect(set.id) ??
    PHONE_ASPECT

  return (
    <section
      aria-label="Set viewer"
      className={`group fixed inset-0 z-40 bg-black text-white ${
        navOpen || faces.asking ? 'sm:[--nav:20rem]' : ''
      } ${
        // The room the faces step's panel takes along the foot of a narrow window.
        faces.asking ? 'max-sm:[--foot:55%]' : ''
      }`}
      onPointerEnter={() => {
        setPointerNear(true)
      }}
      onPointerMove={() => {
        // Belt and braces: an element that appears under a resting cursor is not always
        // entered, and the viewer opens under one every time.
        if (!pointerNear) {
          setPointerNear(true)
        }
      }}
      onPointerLeave={() => {
        setPointerNear(false)
      }}
      onPointerDown={(event) => {
        pointerStart.current = event.clientX
        swiped.current = false
      }}
      onPointerUp={(event) => {
        const start = pointerStart.current
        pointerStart.current = null
        if (start === null) {
          return
        }
        const delta = event.clientX - start
        swiped.current = Math.abs(delta) >= SWIPE_THRESHOLD_PX
        if (faces.asking) {
          return
        }
        if (delta <= -SWIPE_THRESHOLD_PX) {
          goTo(next?.id)
        } else if (delta >= SWIPE_THRESHOLD_PX) {
          goTo(previous?.id)
        }
      }}
    >
      {/*
        The media sits in the largest box of its own shape that the window holds — the video's
        shape, not a guess at it. The box has to be settled before the video loads, or the
        tile flies to a sliver and the picture letterboxes inside it on arrival; the shape the
        tile handed over on the way in is what settles it.
      */}
      <div
        className="absolute inset-0 transition-[right,bottom] duration-300 ease-out"
        style={{ right: 'var(--nav, 0px)', bottom: 'var(--foot, 0px)' }}
      >
        {/*
          Centred by hand rather than by flexbox. A flex item's automatic minimum width comes
          from its contents, and the video's intrinsic width — a thousand-odd pixels of it —
          is a floor that quietly beats the width `aspect-ratio` would work out. The box then
          stays far wider than the video, the video letterboxes inside it, and the arriving
          thumbnail fills what is left over. Positioned absolutely, no such floor applies.

          Opaque, too, and that is load-bearing: the flying thumbnail is held at full opacity
          for the whole trip, so anything it can see through is a blown-up tile.
        */}
        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black"
          style={{
            aspectRatio: String(mediaAspect),
            // The width it has to fit is the window less whatever the panel has taken.
            height: `min(100%, calc((100vw - var(--nav, 0px)) / ${String(mediaAspect)}))`,
            transition: 'height 300ms ease-out',
            // Named whether or not there is a picture: an unfilmed set's card flies into
            // this box too, landing on the slide instead of a poster.
            viewTransitionName: SET_MEDIA_TRANSITION,
          }}
        >
          {/*
            The thumbnail the tile flew here as, holding the frame until the video is
            genuinely playing. Without it the flight would end on a video element that is
            still black, and the picture would blink out at the moment it arrived.
          */}
          {hasPoster && (
            <SafeImage
              src={api.mediaUrl(set.id, 'thumbnail')}
              alt=""
              frameClassName="absolute inset-0 h-full w-full"
              // Filling, so the landing frame has no gaps in it whatever shape it turns out.
              className="absolute inset-0 h-full w-full object-cover"
            />
          )}
          {set.hasVideo ? (
            /*
             * No captions: a set's recording is somebody's phone filming a barbell, and there
             * is no track to point at. It plays out loud — how loud is the device's question.
             */
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              ref={attachVideo}
              aria-label={`Video of ${set.exerciseName} set`}
              src={shownCopyUrl ?? api.mediaUrl(set.id, 'video')}
              className={`relative h-full w-full object-contain transition-[opacity,filter] duration-300 ${
                playingFor === set.id ? 'opacity-100' : 'opacity-0'
              } ${makingCopy ? 'blur-xl' : ''}`}
              data-copy={copyShown?.kind}
              autoPlay
              loop
              playsInline
              onClick={() => {
                // Pressing the picture is the biggest playback control there is.
                if (swiped.current) {
                  return
                }
                // A refused video has nothing to pause, so the press means the one thing left.
                if (refused) {
                  retry()
                  return
                }
                const element = videoRef.current
                if (!element) {
                  return
                }
                if (element.paused) {
                  void element.play()
                } else {
                  element.pause()
                }
              }}
              onError={() => {
                setFailures((seen) => seen + 1)
              }}
              onPlaying={() => {
                setFailures(0)
                setPlayingFor(set.id)
              }}
              onTimeUpdate={(event) => {
                lastMoment.current = {
                  setId: set.id,
                  time: event.currentTarget.currentTime,
                  src: event.currentTarget.currentSrc,
                }
              }}
              onLoadedMetadata={(event) => {
                // Another of this set's videos taking the place of the last: on from its moment.
                const last = lastMoment.current
                if (last?.setId === set.id && last.src !== event.currentTarget.currentSrc) {
                  event.currentTarget.currentTime = last.time
                }
                const { videoWidth, videoHeight } = event.currentTarget
                if (videoWidth > 0 && videoHeight > 0) {
                  const aspect = videoWidth / videoHeight
                  rememberMediaAspect(set.id, aspect)
                  setMeasured({ setId: set.id, aspect })
                }
              }}
            />
          ) : (
            // The numbers belong to the corner block below; this is just the slide, and the
            // mark on it says how the set was logged — or that it was a warm-up, which is the
            // thing worth knowing about it before its method is.
            <span className="flex h-full w-full flex-col items-center justify-center gap-4 px-6 text-center">
              {set.isWarmup ? (
                <FlameIcon aria-label="Warm-up" role="img" className="h-16 w-16 text-white/20" />
              ) : (
                <KindMark records={set.records} named className="h-16 w-16 text-white/20" />
              )}
              <span className="text-4xl font-semibold text-white/15">{set.exerciseName}</span>
            </span>
          )}
          {copyShown?.kind === 'failed' && (
            <div
              role="status"
              className="absolute inset-0 flex items-center justify-center text-white"
            >
              <span className="rounded-xl bg-black/60 px-4 py-2 text-sm">
                The faces could not be blurred in this copy
              </span>
            </div>
          )}
          {copyShown?.kind === 'making' && (
            <div
              role="status"
              className="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black/35 text-white"
            >
              <Spinner className="h-8 w-8" />
              <span className="text-sm">
                Blurring faces… {Math.round(copyShown.progress * 100)}%
              </span>
            </div>
          )}
          {/*
            Both belong to the video rather than to the page: they are sized to the frame and
            they go with the set as the arrows move between them.
          */}
          {notice === 'waiting' && (
            <div
              role="progressbar"
              aria-label="Loading video"
              className="video-wait-line absolute inset-x-0 top-0 h-0.5 overflow-hidden bg-white/20 text-white"
            />
          )}
          {refused && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/45 px-8 text-center text-sm text-white/90">
              {refusalText(notice)}
            </div>
          )}
        </div>
      </div>

      {/*
        Every control rides in this one named layer. During a flight the browser paints
        named layers in page order — chrome after media, so the travelling frame passes
        under the scrims and buttons; unnamed, the chrome would spend the flight beneath
        the picture. The layer itself ignores the pointer so swipes still reach the viewer.
      */}
      <div
        className="pointer-events-none absolute inset-0 transition-[right] duration-300 ease-out"
        style={{
          right: 'var(--nav, 0px)',
          bottom: 'var(--foot, 0px)',
          viewTransitionName: SET_CHROME_TRANSITION,
        }}
      >
        {/*
          The top scrim: close on the left, the actions on the right. Put away while the faces
          step asks; the rest of the chrome, the transport with it, stays working.
        */}
        <div
          aria-hidden={faces.asking || undefined}
          inert={faces.asking || undefined}
          className={`pointer-events-auto absolute inset-x-0 top-0 flex items-center gap-1 bg-gradient-to-b from-black/70 to-transparent p-3 pb-10 transition-opacity ${
            faces.asking ? 'invisible opacity-0' : ''
          }`}
        >
          <button
            type="button"
            aria-label="Close"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition hover:bg-hover-strong hover:text-white"
            onClick={() => {
              close()
            }}
          >
            <CloseIcon className="h-7 w-7" />
          </button>
          {/*
            Keeping the video, rather than looking at it. Fetched and handed over as a file
            rather than linked with `download`: the media lives on the API's own origin, and a
            download attribute across origins is ignored — the browser would navigate to the
            video instead of saving it.
          */}
          {/*
            The way around the workout without leaving the set. It leads the actions because
            it is the one that goes somewhere rather than doing something.
          */}
          <button
            ref={navToggle}
            type="button"
            aria-label="Sets in this workout"
            aria-expanded={navOpen}
            aria-controls="set-viewer-nav"
            className={`ml-auto flex h-10 w-10 items-center justify-center rounded-full transition hover:bg-hover-strong hover:text-white ${
              navOpen ? 'bg-white/20 text-white' : 'text-white/80'
            }`}
            onClick={() => {
              setNavOpen((held) => {
                saveSetViewerNavOpen(!held)
                return !held
              })
            }}
          >
            <ListIcon className="h-7 w-7" />
          </button>
          {set.hasVideo && (
            <WorkingButton
              aria-label="Download"
              spinner="size-6"
              className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition hover:bg-hover-strong hover:text-white"
              onPress={download}
            >
              <DownloadIcon className="h-7 w-7" />
            </WorkingButton>
          )}
          <Link
            to="/sets/$setId/edit"
            params={{ setId: set.id }}
            aria-label="Edit"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition hover:bg-hover-strong hover:text-white"
          >
            <EditIcon className="h-7 w-7" />
          </Link>
          <button
            type="button"
            aria-label="Delete"
            className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition hover:bg-hover-strong hover:text-white"
            onClick={() => {
              setDeleting(true)
            }}
          >
            <DeleteIcon className="h-7 w-7" />
          </button>
        </div>

        {/* Neighbours, up for as long as the pointer is over the viewer. */}
        {!faces.asking && (leafing || previous !== undefined) && (
          <button
            type="button"
            aria-label="Previous set"
            disabled={previous === undefined}
            className={`pointer-events-auto absolute top-1/2 left-3 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-hover-strong disabled:cursor-default disabled:opacity-25 sm:flex ${
              pointerNear ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => {
              goTo(previous?.id)
            }}
          >
            <ChevronLeftIcon className="h-7 w-7" />
          </button>
        )}
        {!faces.asking && (leafing || next !== undefined) && (
          <button
            type="button"
            aria-label="Next set"
            disabled={next === undefined}
            className={`pointer-events-auto absolute top-1/2 right-3 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-hover-strong disabled:cursor-default disabled:opacity-25 sm:flex ${
              pointerNear ? 'opacity-100' : 'opacity-0'
            }`}
            onClick={() => {
              goTo(next?.id)
            }}
          >
            <ChevronRightIcon className="h-7 w-7" />
          </button>
        )}

        {/* Everything the set carries, with the transport beneath it. The related sets stay
            compact and horizontal: context rather than another gallery over the video. */}
        <div className="pointer-events-auto absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 pt-16">
          <div className="max-w-3xl">
            <p className="flex flex-wrap items-center gap-x-1 text-sm font-medium text-white/80">
              <Link
                to="/exercises/$exerciseName"
                params={{ exerciseName: set.exerciseName }}
                className="-ml-2 rounded-lg px-2 py-1 transition-colors hover:bg-white/16 hover:text-white"
              >
                {set.exerciseName}
              </Link>
              {setNumber !== null && (
                <span className="text-white/50">
                  {' · '}
                  {/*
                   * Said in words rather than drawn as a flame and a bare number. A number on
                   * its own beside an icon reads as "1" and leaves you working out what it
                   * counts; the warm-ups are counted apart from the working sets, so which
                   * count this one belongs to is the whole of what the label is for.
                   */}
                  {set.isWarmup ? `Warm-up ${String(setNumber)}` : `Set ${String(setNumber)}`}
                </span>
              )}
            </p>
            <p className="text-3xl font-semibold">{formatSetNumbers(set, units, distanceUnits)}</p>
            {/*
              What is true of the set, a pill apiece. Everything about the day it happened on —
              the body, the walking, the eating — is behind the first of them, which is the day
              itself; the rest are the set's own.
            */}
            <div
              aria-label="About this set"
              className="mt-3 flex flex-wrap items-center gap-2 text-sm"
            >
              <DaySummaryTrigger
                day={setDay}
                moment={{ time: timeOf(set.performedAt), said: 'Before this set' }}
                className={VIEWER_PILL_OPENS}
              >
                {formatDayLabel(set.performedAt)} · {timeOf(set.performedAt)}
              </DaySummaryTrigger>
              {workout !== undefined && (
                <Link
                  to="/workouts/$workoutId"
                  params={{ workoutId: workout.id }}
                  className={`${VIEWER_PILL_OPENS} max-w-64 truncate`}
                >
                  {workoutName}
                </Link>
              )}
              {ranUnder !== null &&
                (enrollment?.templateId == null ? (
                  // A run whose template has gone still names it, and opens nothing.
                  <span className={`${VIEWER_PILL} max-w-64 truncate`}>{ranUnder}</span>
                ) : (
                  <Link
                    to="/templates/$templateId"
                    params={{ templateId: enrollment.templateId }}
                    className={`${VIEWER_PILL_OPENS} max-w-64 truncate`}
                  >
                    {ranUnder}
                  </Link>
                ))}
              {setPills.map((pill) => (
                <span key={pill.key} className={VIEWER_PILL}>
                  {pill.said}
                </span>
              ))}
            </div>
            {set.notes !== null && set.notes.trim() !== '' && (
              <p
                aria-label="Set notes"
                className="mt-2 max-h-24 max-w-2xl overflow-y-auto text-sm leading-relaxed text-white/80"
              >
                {set.notes}
              </p>
            )}
            {/*
              What it was performed to, a card each: the cover at a size worth looking at, the
              title over who it was by — who a track was by is half of knowing which track it
              was. Cards run sideways rather than stacking, so three songs cost the block no
              more height than one. A set that was performed to nothing says nothing, and
              takes no room saying it, so the whole row goes rather than standing empty.
            */}
            {(set.songs ?? []).length > 0 && (
              <div
                className="mt-3"
                // The viewer reads a horizontal drag as "next set"; moving the songs — by a
                // swipe of them, or by the control the row raises — is not that.
                onPointerDown={(event) => {
                  event.stopPropagation()
                }}
                onPointerUp={(event) => {
                  event.stopPropagation()
                }}
              >
                <SidewaysRow label="Songs" className="gap-2">
                  {(set.songs ?? []).map((song, at) => (
                    <SongCard key={`${song.title}-${String(at)}`} song={song} />
                  ))}
                </SidewaysRow>
              </div>
            )}
          </div>
          {set.hasVideo && (
            <PlaybackControls
              video={video}
              videoRef={videoRef}
              onRetry={refused ? retry : undefined}
            />
          )}
        </div>
      </div>

      {/*
        The panel. It slides in from the right and the picture gives up the width it takes —
        on a window too narrow to share, it simply covers, since a sliver of video beside a
        list is neither one thing nor the other.
      */}
      <div
        className={`absolute inset-y-0 right-0 z-10 w-full transition-transform duration-300 ease-out sm:w-80 ${
          navOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        id="set-viewer-nav"
        aria-hidden={!navOpen}
      >
        {/* Only reachable while it is out, so a hidden panel cannot be tabbed into. */}
        <div className={`h-full ${navOpen ? '' : 'invisible'}`}>
          <SetViewerNav
            run={ascendingRun}
            currentId={set.id}
            open={navOpen}
            title={
              workout === undefined ? formatDayLabel(set.performedAt) : (workout.name ?? 'Workout')
            }
            {...(workout === undefined
              ? {}
              : { detail: formatDayLabel(set.performedAt), workoutId: workout.id })}
            units={units}
            distanceUnits={distanceUnits}
            onClose={() => {
              saveSetViewerNavOpen(false)
              setNavOpen(false)
              // Back to the control that opened it. The panel is hidden from the accessibility
              // tree in the same breath, and focus left inside it belongs to nothing.
              navToggle.current?.focus()
            }}
          />
        </div>
      </div>

      {faces.dialog}
      {deleting && (
        <ConfirmDialog
          title="Delete this set?"
          body={`${set.exerciseName}, ${formatSetNumbers(set, units, distanceUnits)}${
            set.hasVideo ? ', and the video of it,' : ''
          } goes for good.`}
          confirm="Delete"
          onConfirm={() => {
            setDeleting(false)
            setDepartingSet(set)
            // No flight on delete: the tile it would fly to is gone with the set.
            void service.deleteSet(set.id).then(
              () => {
                close(false)
                toast('Set deleted')
              },
              () => {
                // It is still there. Put the page back rather than leaving it mid-departure
                // with nothing said.
                setDepartingSet(null)
                toast('That did not reach us. Try again in a moment.')
              },
            )
          }}
          onDismiss={() => {
            setDeleting(false)
          }}
        />
      )}
    </section>
  )
}

/**
 * Seconds as a clock — two figures either side of the colon, and two more in front of them
 * once there are hours: 5 → "00:05", 95 → "01:35", 3725 → "01:02:05". An unknown duration
 * reads as zero.
 *
 * The padding is the whole point of it: a clock never grows or loses a character as it passes
 * ten, so the reading beside it does not shift while it counts.
 */
function formatClock(seconds: number): string {
  const whole = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0
  const pad = (value: number) => String(value).padStart(2, '0')
  const past = `${pad(Math.floor((whole % 3600) / 60))}:${pad(whole % 60)}`
  const hours = Math.floor(whole / 3600)
  return hours > 0 ? `${pad(hours)}:${past}` : past
}

const PLAY_EVENTS = ['play', 'pause', 'ended', 'emptied']
const TIME_EVENTS = ['timeupdate', 'seeking', 'seeked', 'loadedmetadata', 'emptied']
const LENGTH_EVENTS = ['durationchange', 'loadedmetadata', 'canplay', 'progress', 'emptied']
// Every moment a video could start or stop being able to play on: what it is doing has to be
// re-read at all of them, or a stall that nobody announced would go unnoticed.
const STALL_EVENTS = [
  'play',
  'pause',
  'playing',
  'waiting',
  'stalled',
  'canplay',
  'canplaythrough',
  'loadstart',
  'emptied',
  'error',
  'progress',
  'seeking',
  'seeked',
]

/**
 * How long the video runs. A clip recorded in the browser carries no length in its header —
 * `duration` reads as `Infinity` forever — so fall back to how far it can actually seek.
 */
function readDuration(element: HTMLVideoElement | null): number {
  if (element === null) {
    return 0
  }
  if (Number.isFinite(element.duration)) {
    return element.duration
  }
  // Widened on purpose: the DOM types promise this always exists, and jsdom does not have it.
  const seekable = element.seekable as TimeRanges | undefined
  return seekable !== undefined && seekable.length > 0 ? seekable.end(seekable.length - 1) : 0
}

/**
 * One live reading off the video element. It takes the element itself, not a ref: a ref that
 * happened to be empty when the subscription was set up would never be looked at again, and
 * the reading would sit at zero for good — a length stuck at zero is what made the seek bar
 * span a single second and then refuse to move at all.
 */
function useMediaValue<Value>(
  video: HTMLVideoElement | null,
  events: string[],
  read: (video: HTMLVideoElement | null) => Value,
): Value {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (!video) {
        return () => undefined
      }
      for (const event of events) {
        video.addEventListener(event, onChange)
      }
      return () => {
        for (const event of events) {
          video.removeEventListener(event, onChange)
        }
      }
    },
    [video, events],
  )

  return useSyncExternalStore(
    subscribe,
    () => read(video),
    () => read(null),
  )
}

/**
 * Play, pause, seek, and the elapsed and total time. The video element is the source of
 * truth — this only reads it, so pausing from anywhere keeps the button honest.
 */
function PlaybackControls({
  video,
  videoRef,
  onRetry,
}: {
  video: HTMLVideoElement | null
  videoRef: RefObject<HTMLVideoElement | null>
  /*
   * Set only where the video has stopped playing and will not start. Everything here that
   * would have moved it tries it again instead — the play control, which says as much by
   * becoming a retry, and the seek bar, which cannot say it but means it all the same.
   */
  onRetry?: (() => void) | undefined
}) {
  const playing = useMediaValue(video, PLAY_EVENTS, (element) => (element ? !element.paused : true))
  const elapsed = useMediaValue(video, TIME_EVENTS, (element) => element?.currentTime ?? 0)
  // A reading of NaN would never equal itself, and the store would re-render forever.
  const duration = useMediaValue(video, LENGTH_EVENTS, readDuration)

  // While a thumb is held the pointer owns the position: a browser may take its time
  // honouring a seek, and the bar must not spring back under the finger meanwhile.
  const [dragging, setDragging] = useState<number | null>(null)
  const seekMax = duration > 0 ? duration : 1
  const shown = dragging ?? Math.min(elapsed, seekMax)

  return (
    <div
      className="mt-4 flex items-center gap-3"
      // The viewer reads a horizontal drag as "next set"; a seek drag is not that.
      onPointerDown={(event) => {
        event.stopPropagation()
      }}
      onPointerUp={(event) => {
        event.stopPropagation()
      }}
    >
      <button
        type="button"
        aria-label={onRetry ? 'Retry' : playing ? 'Pause' : 'Play'}
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-hover-strong"
        onClick={() => {
          if (onRetry) {
            onRetry()
            return
          }
          const video = videoRef.current
          if (!video) {
            return
          }
          if (video.paused) {
            void video.play()
          } else {
            video.pause()
          }
        }}
      >
        {onRetry ? (
          <RetryIcon className="h-6 w-6" />
        ) : playing ? (
          <PauseIcon className="h-7 w-7" />
        ) : (
          <PlayIcon className="h-7 w-7" />
        )}
      </button>
      <span className="w-11 shrink-0 text-right text-xs tabular-nums text-white/70">
        {formatClock(shown)}
      </span>
      <input
        type="range"
        aria-label="Seek"
        min={0}
        max={seekMax}
        step={0.05}
        value={shown}
        // Until the length is known every position on the bar means the same instant, so
        // dragging could only ever throw playback back to the start. A refused video has
        // nowhere to be dragged to either, but the bar stays reachable: touching it is one
        // more way of saying "go", and it is answered as one.
        disabled={duration === 0 && !onRetry}
        onChange={(event) => {
          if (onRetry) {
            onRetry()
            return
          }
          const next = Number(event.target.value)
          setDragging(next)
          const element = videoRef.current
          if (element) {
            element.currentTime = next
          }
        }}
        // Released: the media clock drives the bar again.
        onPointerUp={() => {
          setDragging(null)
        }}
        onPointerCancel={() => {
          setDragging(null)
        }}
        onKeyUp={() => {
          setDragging(null)
        }}
        onBlur={() => {
          setDragging(null)
        }}
        className="h-1 w-full accent-white disabled:opacity-50"
      />
      <span className="w-11 shrink-0 text-xs tabular-nums text-white/70">
        {formatClock(duration)}
      </span>
    </div>
  )
}
